import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/http';
import { useChannelScopes, useHelixCatalog } from '../../api/hooks';
import {
  annotateFields,
  getPath,
  injectedFields,
  serializeHelixParams,
  setPath,
  visibleFields,
  type HelixFormField,
} from '../../domain/helixForm';
import type { HelixEndpoint } from '../../domain/types';
import { Badge, Button, Dialog, ErrorNote, Field, Input, JsonBlock, Loading, SplitPane } from '../../ui';
import { helixTitle } from './labels';
import { missingScopes } from '../../domain/scopeAlgebra';
import { DurationPicker, GamePicker, RepeatableList, ScalarField, TokenList, UserPicker } from './widgets';

export function ChannelHelixPage(): JSX.Element {
  const { botId = '', channelId = '' } = useParams();
  const catalog = useHelixCatalog();
  const scopes = useChannelScopes(botId, channelId);
  const [query, setQuery] = useState('');
  const [endpoint, setEndpoint] = useState<string>('');
  const [detail, setDetail] = useState<HelixEndpoint | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [permanent, setPermanent] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [rate, setRate] = useState<{ remaining: string | null; limit: string | null }>({ remaining: null, limit: null });
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickedLogin, setPickedLogin] = useState('');

  const granted = scopes.data?.granted ?? [];
  const endpoints = catalog.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return endpoints.filter((item) => {
      if (!q) return true;
      return [item.endpoint, item.category, item.description, item.operation].some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [endpoints, query]);
  const groups = useMemo(() => {
    const map = new Map<string, HelixEndpoint[]>();
    for (const item of filtered) {
      const cat = item.category || 'autre';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  useEffect(() => {
    if (!endpoint) {
      setDetail(null);
      return;
    }
    setValues({});
    setPermanent(false);
    setResult(null);
    setError(null);
    void api.helixEndpoint(endpoint).then(setDetail).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [endpoint]);

  if (catalog.isPending) return <Loading />;
  const fields = annotateFields([...(detail?.formSchema?.query ?? detail?.query ?? []), ...(detail?.formSchema?.body ?? detail?.body ?? [])]);
  const visible = visibleFields(fields);
  const injected = injectedFields(fields);
  const mutating = (detail?.method ?? 'GET') !== 'GET';
  const scopeGap = missingScopes(detail?.scopes ?? [], granted);

  const payload = serializeHelixParams(fields, values, { permanentBan: permanent });
  const userLogin = pickedLogin || String(getPath(values, ['data', 'user_id']) ?? '');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.callHelix(botId, channelId, endpoint, payload);
      setResult(res.body);
      setRate({ remaining: res.rateLimit.remaining, limit: res.rateLimit.limit });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <SplitPane
      side={
        <div className="tbm-helix-nav">
          <Input value={query} placeholder="Rechercher une opération" onChange={(e) => setQuery(e.target.value)} />
          {groups.map(([cat, items]) => (
            <details key={cat} className="tbm-acc" open>
              <summary>{cat}</summary>
              <ul className="tbm-helix-ops">
                {items.map((item) => {
                  const gap = missingScopes(item.scopes ?? [], granted).length > 0;
                  const dead = item.lifecycle?.status === 'deprecated' || item.lifecycle?.status === 'decommissioned';
                  return (
                    <li key={item.endpoint}>
                      <button
                        type="button"
                        className={`tbm-helix-op${endpoint === item.endpoint ? ' is-active' : ''}${gap || dead ? ' is-disabled' : ''}`}
                        title={gap ? `Scope manquant : ${(item.scopes ?? []).join(', ')}` : helixTitle(item.endpoint)}
                        onClick={() => setEndpoint(item.endpoint)}
                      >
                        <span>{helixTitle(item.endpoint)}</span>
                        <span className="tbm-helix-op__badges">
                          <Badge>{item.permission === 'write' ? 'écriture' : 'lecture'}</Badge>
                          <Badge>{item.actor === 'broadcaster' ? 'diffuseur' : 'bot'}</Badge>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </div>
      }
    >
      {!detail ? (
        <div className="tbm-helix-form">
          <p className="muted">Choisissez une opération dans le navigateur.</p>
        </div>
      ) : (
        <div className="tbm-helix-form">
          <header className="tbm-section__head">
            <div>
              <h2>{helixTitle(detail.endpoint)}</h2>
              <p className="muted">{detail.description || `${detail.method} ${detail.path}`}</p>
              <p className="muted">{detail.method} {detail.path} · {(detail.scopes ?? []).join(', ') || 'aucun scope'}</p>
              {detail.docUrl ? <a href={detail.docUrl} target="_blank" rel="noreferrer">Documentation Twitch</a> : null}
            </div>
            {rate.remaining != null ? (
              <span className="tbm-quota">Quota {rate.remaining}/{rate.limit}</span>
            ) : null}
          </header>
          {scopeGap.length ? (
            <ErrorNote>
              Scopes manquants : {scopeGap.join(', ')}. <Link to={`/bots/${botId}/channels/${channelId}/scopes`}>Réautoriser cette chaîne</Link>
            </ErrorNote>
          ) : null}
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          <form
            className="tbm-form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              if (mutating) setConfirm(true);
              else void submit();
            }}
          >
            {visible.map((field) => (
              <HelixField
                key={field.name}
                field={field}
                path={[field.name]}
                values={values}
                setValues={setValues}
                botId={botId}
                channelId={channelId}
                permanent={permanent}
                onPermanent={setPermanent}
                onUserLogin={setPickedLogin}
              />
            ))}
            {injected.length ? (
              <details>
                <summary>Paramètres avancés</summary>
                {injected.map((field) => (
                  <Field key={field.name} label={field.name} hint="Injecté automatiquement. Forcer une valeur si nécessaire.">
                    <Input
                      value={String(getPath(values, [field.name]) ?? '')}
                      onChange={(e) => setValues((cur) => setPath(cur, [field.name], e.target.value))}
                    />
                  </Field>
                ))}
              </details>
            ) : null}
            <Button variant="primary" type="submit" loading={busy} disabled={Boolean(scopeGap.length)}>
              {mutating ? 'Préparer l’envoi' : 'Appeler'}
            </Button>
          </form>
          {result !== null && result !== undefined ? <HelixResult body={result} fields={detail.response ?? []} /> : null}
          <Dialog
            open={confirm}
            title="Confirmer l’action"
            confirmLabel="Envoyer"
            onClose={() => setConfirm(false)}
            onConfirm={() => void submit()}
          >
            <p>{confirmSummary(detail.endpoint, userLogin, permanent, payload)}</p>
          </Dialog>
        </div>
      )}
    </SplitPane>
  );
}

function HelixField({
  field,
  path,
  values,
  setValues,
  botId,
  channelId,
  permanent,
  onPermanent,
  onUserLogin,
}: {
  field: HelixFormField;
  path: string[];
  values: Record<string, unknown>;
  setValues: (fn: (cur: Record<string, unknown>) => Record<string, unknown>) => void;
  botId: string;
  channelId: string;
  permanent: boolean;
  onPermanent: (v: boolean) => void;
  onUserLogin: (login: string) => void;
}): JSX.Element {
  const widget = field.widget ?? 'input';
  const value = getPath(values, path);
  const set = (next: unknown): void => setValues((cur) => setPath(cur, path, next));
  if (widget === 'user') {
    return (
      <UserPicker
        botId={botId}
        channelId={channelId}
        value={String(value ?? '')}
        label={field.name}
        required={field.required}
        hint={field.hint}
        onChange={(id, login) => {
          set(id);
          if (login) onUserLogin(login);
        }}
      />
    );
  }
  if (widget === 'game') {
    return (
      <GamePicker botId={botId} channelId={channelId} value={String(value ?? '')} onChange={set} label={field.name} required={field.required} hint={field.hint} />
    );
  }
  if (widget === 'duration') {
    return (
      <DurationPicker
        value={typeof value === 'number' ? value : ''}
        onChange={set}
        permanent={permanent}
        onPermanent={onPermanent}
        label={field.name}
        hint={field.hint}
      />
    );
  }
  if (widget === 'tokens') {
    return <TokenList values={Array.isArray(value) ? value.map(String) : []} onChange={set} label={field.name} hint={field.hint} />;
  }
  if (widget === 'repeatable') {
    return <RepeatableList field={field} values={Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []} onChange={set} />;
  }
  if (field.children?.length) {
    return (
      <fieldset className="tbm-fieldset">
        <legend>{field.name}</legend>
        {visibleFields(field.children).map((child) => (
          <HelixField
            key={child.name}
            field={child}
            path={[...path, child.name]}
            values={values}
            setValues={setValues}
            botId={botId}
            channelId={channelId}
            permanent={permanent}
            onPermanent={onPermanent}
            onUserLogin={onUserLogin}
          />
        ))}
      </fieldset>
    );
  }
  return <ScalarField field={field} value={value} onChange={set} />;
}

function HelixResult({ body, fields }: { body: unknown; fields: HelixFormField[] }): JSX.Element {
  const data = isRecord(body) && Array.isArray(body.data) ? body.data : null;
  if (data) {
    const cols = fields.length ? fields.map((f) => f.name) : Object.keys((data[0] as Record<string, unknown>) ?? {});
    return (
      <div>
        <table className="tbm-table">
          <thead>
            <tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                {cols.map((c) => <td key={c}>{isRecord(row) ? String(row[c] ?? '') : ''}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        <JsonBlock value={body} />
      </div>
    );
  }
  return <JsonBlock value={body} />;
}

function confirmSummary(endpoint: string, login: string, permanent: boolean, payload: Record<string, unknown>): string {
  const who = login || 'cet utilisateur';
  if (endpoint === 'moderation.banUser') {
    const duration = (payload.data as { duration?: number } | undefined)?.duration;
    return permanent || !duration
      ? `Bannir définitivement ${who} de cette chaîne`
      : `Exclure ${who} pendant ${Math.round(duration / 60)} minutes`;
  }
  return `Exécuter ${helixTitle(endpoint)} sur cette chaîne`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export { ChannelHelixPage as HelixPage };
